import { useSignalR } from '../components/providers/SignalRProvider.vue'
import type { SignalRReceiveEvent, SubscribeToEventOptions } from '../store'
import { ref } from 'vue'
import type {
  SettingRemovedSignalRPayload,
  SettingSetSignalRPayload
} from '@gat-ship/types/signal-r-events'
import { signalREvent } from '@gat-ship/types/signal-r-events'

export type SettingsEventHandlers = {
  onSettingRemoved?: (
    payload: SignalRReceiveEvent<SettingRemovedSignalRPayload>
  ) => void
  onSettingSet?: (
    payload: SignalRReceiveEvent<SettingSetSignalRPayload>
  ) => void
}

export function useSettingsSignalREvents(handlers?: SettingsEventHandlers) {
  const { subscribe } = useSignalR()

  const subscription = ref<() => void>()

  function subscribeToSettingRemoved(
    cb: (payload: SignalRReceiveEvent<SettingRemovedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<SettingRemovedSignalRPayload>(
      signalREvent.Settings.V1.SettingRemoved,
      cb,
      options
    )
  }

  function subscribeToSettingSet(
    cb: (payload: SignalRReceiveEvent<SettingSetSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<SettingSetSignalRPayload>(
      signalREvent.Settings.V1.SettingSet,
      cb,
      options
    )
  }

  function subscribeToSettingsEvents() {
    if (subscription.value) {
      return subscription.value
    }

    if (handlers) {
      const unsubscribeFunctions: (() => void)[] = []

      if (handlers.onSettingRemoved) {
        unsubscribeFunctions.push(
          subscribeToSettingRemoved(handlers.onSettingRemoved)
        )
      }
      if (handlers.onSettingSet) {
        unsubscribeFunctions.push(subscribeToSettingSet(handlers.onSettingSet))
      }

      subscription.value = () => {
        unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
      }
    }

    return subscription.value
  }

  function unsubscribeFromSettingsEvents() {
    subscription.value?.()
    subscription.value = undefined
  }

  return {
    subscribeToSettingsEvents,
    unsubscribeFromSettingsEvents
  }
}
